// React
import { useState } from "react";

// Third-Party
import {
  Backdrop,
  Box,
  CircularProgress,
  Collapse,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, LogOut, LucideIcon, Users } from "lucide-react";

// Internal
import {
  getRunningAccount,
  hasOtherAccounts,
  hasUnsavedChanges,
  signOutAccount,
} from "src/common/accountSession";
import useLocales from "src/hooks/useLocales";

// ----------------------------------------------------------------------

type TItemProps = {
  icon: LucideIcon;
  title: string;
  onClick: () => void;
  endIcon?: React.ReactNode;
  inset?: boolean;
};

export const AccountMenuItem = ({
  icon: Icon,
  title,
  onClick,
  endIcon,
  inset,
}: TItemProps) => {
  const theme = useTheme();
  return (
    <MenuItem onClick={onClick} sx={inset ? { pl: 3 } : undefined}>
      <Box sx={{ "& svg": { mr: 2, width: 20, height: 20 } }}>
        <Icon color={theme.palette.text.label} strokeWidth={1.5} />
      </Box>
      <Typography variant="body2" color="text.label" noWrap sx={{ flexGrow: 1 }}>
        {title}
      </Typography>
      {endIcon}
    </MenuItem>
  );
};

type TProps = {
  onLogoutAll: () => void;
  isOpen: boolean;
  onToggle: () => void;
};

export default function AccountLogoutMenu({
  onLogoutAll,
  isOpen,
  onToggle,
}: TProps) {
  const theme = useTheme();
  const { translate } = useLocales();
  const [isLeaving, setIsLeaving] = useState(false);

  if (!hasOtherAccounts()) {
    return <AccountMenuItem icon={LogOut} title={translate("Logout")} onClick={onLogoutAll} />;
  }

  const current = getRunningAccount();
  const leave = (action: () => void, canStay = false) => {
    if (!canStay || !hasUnsavedChanges()) setIsLeaving(true);
    action();
  };

  return (
    <>
      <AccountMenuItem
        icon={LogOut}
        title={translate("Logout")}
        onClick={onToggle}
        endIcon={
          <ChevronDown
            size={16}
            color={theme.palette.text.label}
            style={{
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "none",
            }}
          />
        }
      />
      <Collapse in={isOpen} unmountOnExit>
        <AccountMenuItem
          inset
          icon={LogOut}
          title={translate("multi_account.logout_current", {
            email: current?.email ?? "",
          })}
          onClick={() => leave(() => signOutAccount(), true)}
        />
        <AccountMenuItem
          inset
          icon={Users}
          title={translate("multi_account.logout_all")}
          onClick={() => leave(onLogoutAll)}
        />
      </Collapse>
      <Backdrop open={isLeaving} sx={{ zIndex: theme.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
